// Reading a saved song analysis at any moment of the song: what the lights
// need at time t (loudness, bass, the note playing, where we are in the beat,
// bar and section). Pure code with no DOM or Node APIs, so the Pi agent and
// the browser share it. Levels are as analyzed (frame to frame, 20 per
// second); smoothing for the lights is left to the caller.
//
//   const song = decodeSong(analysis);        // once per song
//   const f = featuresAt(song, seconds);      // every frame

import { decodeBase64, NO_PITCH, type AnalysisSection, type SavedAnalysis } from "./library.ts";

export const BEATS_PER_BAR = 4;

export interface DecodedSong {
  duration: number;
  bpm: number;
  frameRate: number;
  frames: number;
  beats: Float64Array;
  // For each bar, the index in beats of its first beat.
  barStarts: Int32Array;
  sections: AnalysisSection[];
  // Continuous features, 0-1 per frame.
  loudness: Float32Array;
  bass: Float32Array;
  mid: Float32Array;
  treble: Float32Array;
  onset: Float32Array;
  register: Float32Array;
  pitchConfidence: Float32Array;
  // 0-11 (C to B), or NO_PITCH.
  pitchClass: Uint8Array;
}

export interface SongFeatures {
  // 0-1, interpolated between frames; 0 before the start and after the end.
  loudness: number;
  bass: number;
  mid: number;
  treble: number;
  onset: number;
  register: number;
  pitchConfidence: number;
  // 0-11 (C, C#, ... B) of the nearest frame, or null when no note stands out.
  pitchClass: number | null;
  // Index into beats of the current beat; -1 before the first beat. After the
  // last beat it stays on the last one.
  beatIndex: number;
  // 0-1 through the current beat. Before the first beat it rises toward 1 over
  // the beat length leading in; after the last beat it holds at 1 once that
  // beat's length has passed.
  beatPhase: number;
  // Length of the current beat in seconds (the first or last beat's length
  // before and after the beats).
  beatSeconds: number;
  // Index of the current bar; -1 in the pickup before the first downbeat.
  barIndex: number;
  // 0-1 through the bar, moving with the beats.
  barPhase: number;
  sectionIndex: number;
  sectionLabel: string;
  sectionEnergy: number;
}

export function decodeSong(analysis: SavedAnalysis): DecodedSong {
  const bytes = (name: keyof SavedAnalysis["features"]): Uint8Array => {
    const decoded = decodeBase64(analysis.features[name]);
    if (!decoded) throw new Error(`analysis feature ${name} is not base64`);
    return decoded;
  };
  const unit = (name: keyof SavedAnalysis["features"]): Float32Array => {
    const raw = bytes(name);
    const out = new Float32Array(raw.length);
    for (let i = 0; i < raw.length; i++) out[i] = raw[i]! / 255;
    return out;
  };
  const beats = Float64Array.from(analysis.beats);
  // Downbeats are a subset of beats (parseAnalysis checks); find their indices.
  const barStarts: number[] = [];
  let b = 0;
  for (const down of analysis.downbeats) {
    while (b < beats.length && beats[b]! < down) b++;
    if (b < beats.length && beats[b] === down) barStarts.push(b);
  }
  const pitchClass = bytes("pitchClass");
  return {
    duration: analysis.duration,
    bpm: analysis.bpm,
    frameRate: analysis.frameRate,
    frames: pitchClass.length,
    beats,
    barStarts: Int32Array.from(barStarts),
    sections: analysis.sections.length > 0 ? analysis.sections : [{ start: 0, label: "A", energy: 0 }],
    loudness: unit("loudness"),
    bass: unit("bass"),
    mid: unit("mid"),
    treble: unit("treble"),
    onset: unit("onset"),
    register: unit("register"),
    pitchConfidence: unit("pitchConfidence"),
    pitchClass,
  };
}

export function featuresAt(song: DecodedSong, t: number): SongFeatures {
  const playing = t >= 0 && t <= song.duration && song.frames > 0;
  // Frame position, clamped to the frames there are.
  const x = Math.min(song.frames - 1, Math.max(0, t * song.frameRate));
  const i = Math.floor(x);
  const f = x - i;
  const j = Math.min(song.frames - 1, i + 1);
  const at = (a: Float32Array) => (playing ? a[i]! * (1 - f) + a[j]! * f : 0);
  const pitch = playing ? song.pitchClass[f < 0.5 ? i : j]! : NO_PITCH;

  const { beatIndex, beatPhase, beatSeconds } = beatAt(song.beats, t, song.bpm);
  const { barIndex, barPhase } = barAt(song.barStarts, beatIndex, beatPhase);

  const { sections } = song;
  let s = 0;
  while (s + 1 < sections.length && sections[s + 1]!.start <= t) s++;
  const section = sections[s]!;

  return {
    loudness: at(song.loudness),
    bass: at(song.bass),
    mid: at(song.mid),
    treble: at(song.treble),
    onset: at(song.onset),
    register: at(song.register),
    pitchConfidence: at(song.pitchConfidence),
    pitchClass: pitch === NO_PITCH ? null : pitch,
    beatIndex,
    beatPhase,
    beatSeconds,
    barIndex,
    barPhase,
    sectionIndex: s,
    sectionLabel: section.label,
    sectionEnergy: section.energy,
  };
}

function beatAt(beats: Float64Array, t: number, bpm: number): { beatIndex: number; beatPhase: number; beatSeconds: number } {
  const n = beats.length;
  if (n === 0) return { beatIndex: -1, beatPhase: 0, beatSeconds: 60 / bpm };
  // Beat length before the first and after the last beat.
  const edge = (a: number, b: number) => (n > 1 ? beats[b]! - beats[a]! : 60 / bpm);
  if (t < beats[0]!) {
    const length = edge(0, 1);
    return { beatIndex: -1, beatPhase: clamp01(1 - (beats[0]! - t) / length), beatSeconds: length };
  }
  // Last beat at or before t (binary search).
  let lo = 0;
  let hi = n - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (beats[mid]! <= t) lo = mid;
    else hi = mid - 1;
  }
  const length = lo + 1 < n ? beats[lo + 1]! - beats[lo]! : edge(n - 2, n - 1);
  return { beatIndex: lo, beatPhase: clamp01((t - beats[lo]!) / length), beatSeconds: length };
}

function barAt(barStarts: Int32Array, beatIndex: number, beatPhase: number): { barIndex: number; barPhase: number } {
  if (barStarts.length === 0) return { barIndex: -1, barPhase: 0 };
  // Last bar starting at or before this beat (binary search), or -1.
  let bar = -1;
  let hi = barStarts.length - 1;
  while (bar < hi) {
    const mid = (bar + hi + 1) >> 1;
    if (barStarts[mid]! <= beatIndex) bar = mid;
    else hi = mid - 1;
  }
  // The pickup before the first downbeat counts as the end of a bar -1.
  const start = bar >= 0 ? barStarts[bar]! : barStarts[0]! - BEATS_PER_BAR;
  const next = bar + 1 < barStarts.length ? barStarts[bar + 1]! : start + BEATS_PER_BAR;
  return { barIndex: bar, barPhase: clamp01((beatIndex - start + beatPhase) / (next - start)) };
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
